import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, RefObject } from 'react';
import { DEFAULT_ASSISTANT_SETTINGS, getStandardAssistantIntro, normalizeAssistantSettings } from '@course-authoring/shared-types';
import type { AssistantSettings } from '@course-authoring/shared-types';
import type { AccountThemeDto } from '../../api/accountThemeApi';
import type { ThemeAssetDto } from '../../api/themeAssetApi';

const AVATAR_ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif']);
const AVATAR_MAX_BYTES = 2 * 1024 * 1024;

export function limitAssistantText(value: string, limit: number): string {
  let result = '';
  let units = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint >= 0xd800 && codePoint <= 0xdfff) continue;
    if (units + character.length > limit) break;
    result += character;
    units += character.length;
  }
  return result;
}

export interface StagedAssistantAvatar {
  src: string;
  name: string;
  assetId: number | null;
  file?: File;
}

export interface ThemeAssistantSectionViewModel {
  settings: AssistantSettings;
  avatar: StagedAssistantAvatar | null;
  avatarError: string | null;
  avatarInputRef: RefObject<HTMLInputElement | null>;
  avatarInputId: string;
  setName: (name: string) => void;
  setGreeting: (greeting: string) => void;
  setIntro: (intro: string) => void;
  setIntroStandard: () => void;
  avatarHandlers: {
    triggerFilePicker: () => void;
    handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
    handleDelete: () => void;
  };
  seedFromDto: (dto: AccountThemeDto | undefined) => void;
  avatarAssetId: number | null;
  avatarFile: File | null;
}

function revokeAvatar(avatar: StagedAssistantAvatar | null): void {
  if (avatar?.file) URL.revokeObjectURL(avatar.src);
}

export function useThemeAssistantSettings(disabled = false): ThemeAssistantSectionViewModel {
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const [settings, setSettings] = useState<AssistantSettings>({ ...DEFAULT_ASSISTANT_SETTINGS });
  const [avatar, setAvatar] = useState<StagedAssistantAvatar | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  useEffect(() => () => revokeAvatar(avatar), [avatar?.src]);

  const setName = useCallback((name: string) => {
    if (disabledRef.current) return;
    setSettings((current) => ({
      ...current,
      name: limitAssistantText(name, 12),
      intro: current.introMode === 'standard' ? getStandardAssistantIntro(limitAssistantText(name, 12)) : current.intro,
    }));
  }, []);

  const setGreeting = useCallback((greeting: string) => {
    if (!disabledRef.current) setSettings((current) => ({ ...current, greeting: limitAssistantText(greeting, 40) }));
  }, []);

  const setIntro = useCallback((intro: string) => {
    if (!disabledRef.current) setSettings((current) => ({ ...current, introMode: 'custom', intro: limitAssistantText(intro, 160) }));
  }, []);

  const setIntroStandard = useCallback(() => {
    if (!disabledRef.current) {
      setSettings((current) => ({
        ...current,
        introMode: 'standard',
        intro: getStandardAssistantIntro(current.name),
      }));
    }
  }, []);

  const seedFromDto = useCallback((dto: AccountThemeDto | undefined) => {
    setSettings(normalizeAssistantSettings(dto?.assistant));
    revokeAvatar(avatar);
    const savedAvatar: ThemeAssetDto | null = dto?.assistantAvatar ?? null;
    setAvatar(savedAvatar ? {
      src: savedAvatar.url,
      name: savedAvatar.filename,
      assetId: savedAvatar.id,
    } : null);
    setAvatarError(null);
  }, [avatar]);

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || disabledRef.current) return;
    if (!AVATAR_ALLOWED_TYPES.has(file.type)) {
      setAvatarError('Unsupported file type. Please upload a PNG, JPG, or GIF.');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarError('File is too large. Maximum size is 2MB.');
      return;
    }
    setAvatarError(null);
    const next: StagedAssistantAvatar = {
      src: URL.createObjectURL(file),
      name: file.name,
      assetId: null,
      file,
    };
    setAvatar((current) => {
      revokeAvatar(current);
      return next;
    });
  }, []);

  const handleDelete = useCallback(() => {
    if (disabledRef.current) return;
    setAvatar((current) => {
      revokeAvatar(current);
      return null;
    });
    setAvatarError(null);
  }, []);

  return {
    settings,
    avatar,
    avatarError,
    avatarInputRef,
    avatarInputId: 'theme-assistant-avatar',
    setName,
    setGreeting,
    setIntro,
    setIntroStandard,
    avatarHandlers: {
      triggerFilePicker: () => avatarInputRef.current?.click(),
      handleInputChange,
      handleDelete,
    },
    seedFromDto,
    avatarAssetId: avatar?.assetId ?? null,
    avatarFile: avatar?.file ?? null,
  };
}
